import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout

  useEffect(() => {
    let tries = 0;
    const poll = async () => {
      if (!sid) return setState("timeout");
      try {
        const r = await api.paymentStatus(sid);
        if (r.payment_status === "paid") {
          await refresh();
          return setState("paid");
        }
      } catch { /* ignore */ }
      if (++tries > 10) return setState("timeout");
      setTimeout(poll, 2000);
    };
    poll();
  }, [sid]);

  return (
    <Shell>
      {state === "checking" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-[#E8833A] mx-auto" />
          <h1 className="font-display font-bold text-2xl mt-5">Confirming your payment…</h1>
          <p className="text-[#9A9CA6] mt-2 text-sm">This takes just a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12 text-[#4ADE80] mx-auto" />
          <h1 className="font-display font-bold text-2xl mt-5">You're all set!</h1>
          <p className="text-[#9A9CA6] mt-2 text-sm">Your credits have been added to your account.</p>
          <Link to="/app" data-testid="payment-launch-app" className="inline-block mt-6 px-6 py-3 rounded-lg font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors">
            Go to dashboard
          </Link>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 className="h-10 w-10 text-[#9A9CA6] mx-auto" />
          <h1 className="font-display font-bold text-2xl mt-5">Still processing</h1>
          <p className="text-[#9A9CA6] mt-2 text-sm">Your payment is taking longer than usual. Your credits will appear shortly.</p>
          <Link to="/app" className="inline-block mt-6 px-6 py-3 rounded-lg font-semibold bg-[#1A1D26] border border-[#262933] text-[#F3EFE6] hover:border-[#E8833A]/50 transition-colors">
            Back to dashboard
          </Link>
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle className="h-12 w-12 text-[#FB7185] mx-auto" />
      <h1 className="font-display font-bold text-2xl mt-5">Payment cancelled</h1>
      <p className="text-[#9A9CA6] mt-2 text-sm">No charge was made. You can try again anytime.</p>
      <Link to="/app" className="inline-block mt-6 px-6 py-3 rounded-lg font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors">
        Back to dashboard
      </Link>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0B0D11] text-[#F3EFE6] flex items-center justify-center px-5">
    <div className="text-center max-w-md">{children}</div>
  </div>
);
